import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  Applications,
  WhyChooseLiftile,
  QualitySafetyCommitment,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  ArrowUp,
  Award,
  BadgeCheck,
  Boxes,
  Building2,
  ClipboardCheck,
  Cog,
  Cpu,
  Disc,
  Factory,
  Flag,
  Frame,
  Hammer,
  HardHat,
  Home,
  Landmark,
  Package,
  Ruler,
  ShieldCheck,
  Sparkles,
  Target,
  Wrench,
  Zap,
} from "lucide-react";

/* =====================================================================
   BAR BENDING MACHINE (LRB Series — LRB40 / LRB50) — dedicated product page.
   Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-bar-bending.jpg";

const HERO_SPECS = [
  { Icon: Cog, value: "Up to 42 mm", label: "Max TMT Bar Diameter" },
  { Icon: Zap, value: "5–6.5 HP", label: "Motor Power" },
  { Icon: Package, value: "2 Ton/hour", label: "Production Capacity" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Automatic Operation",
  "Heavy Duty Construction",
  "Premium Components",
  "Made in India",
];

const GALLERY = [
  {
    src: "/product-bar-bending.jpg",
    caption:
      "LIFTILE LRB Series Bar Bending Machine — precision rebar bending for construction sites",
  },
];

const QUICK_SPECS = [
  { label: "Bar Diameter (TMT)", value: "Up to 42 mm" },
  { label: "Motor Power", value: "5–6.5 HP" },
  { label: "Motor Speed", value: "1440 RPM" },
  { label: "Production", value: "2 Ton/hour" },
];

const MODELS = {
  LRB40: {
    name: "LRB40",
    tagline: "Reliable 32 mm TMT bar bending workhorse for mid-scale sites",
    rows: [
      { label: "Bar Bending Diameter (TMT)", value: "Up to 32 mm" },
      { label: "Bar Bending Diameter (Plain Bar)", value: "Up to 35 mm" },
      { label: "Motor", value: "5 HP" },
      { label: "Motor Speed", value: "1440 RPM" },
      { label: "Production Capacity", value: "2 Ton/hour" },
      { label: "Operation", value: "Automatic" },
    ],
  },
  LRB50: {
    name: "LRB50",
    tagline: "Heavy-duty 42 mm TMT flagship for high-volume infrastructure sites",
    rows: [
      { label: "Bar Bending Diameter (TMT)", value: "Up to 42 mm" },
      { label: "Bar Bending Diameter (Plain Bar)", value: "Up to 45 mm" },
      { label: "Motor", value: "6.5 HP" },
      { label: "Motor Speed", value: "1440 RPM" },
      { label: "Production Capacity", value: "2 Ton/hour" },
      { label: "Operation", value: "Automatic" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Frame,
    title: "Heavy Duty Fabricated Steel Body",
    desc: "Precision-welded heavy steel body engineered for rigidity, vibration control and long service life on demanding sites.",
  },
  {
    Icon: Cog,
    title: "High Torque Gear System",
    desc: "Robust reduction gear train delivers the torque needed for accurate bending of large-diameter TMT bars.",
  },
  {
    Icon: Cpu,
    title: "Automatic Operation",
    desc: "Automatic bending cycle with adjustable angle settings for consistent output and reduced operator fatigue.",
  },
  {
    Icon: Target,
    title: "Accurate Bending Angles",
    desc: "Precision bending mechanism delivers consistent, repeatable angles across every bar for reliable rebar fabrication.",
  },
  {
    Icon: Zap,
    title: "Premium Copper Winding Motor",
    desc: "Industrial-grade copper winding electric motor delivers dependable torque, efficiency and long service life.",
  },
  {
    Icon: Disc,
    title: "Hardened Bending Disc",
    desc: "Wear-resistant hardened bending disc holds tight tolerances and lasts through years of continuous operation.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance",
    desc: "Simple mechanical design with easy access to service points keeps downtime and operating costs low.",
  },
  {
    Icon: Cog,
    title: "Heavy Duty Gearbox",
    desc: "Sealed heavy-duty gearbox built for continuous industrial duty cycles and demanding rebar operations.",
  },
  {
    Icon: Sparkles,
    title: "Powder Coated Finish",
    desc: "7-tank pre-treatment with durable powder coating for site-grade corrosion resistance and a professional look.",
  },
  {
    Icon: Boxes,
    title: "Compact Industrial Design",
    desc: "Small footprint with integrated base — easy to position and relocate on tight construction sites.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LRB Series machine is personally inspected and quality signed off before it leaves the factory.",
  },
  {
    Icon: BadgeCheck,
    title: "Tested Before Dispatch",
    desc: "Full trial-run testing on every machine — verifying motor, gear system, bending accuracy and safety.",
  },
];

const APPLICATIONS = [
  {
    Icon: Home,
    title: "Residential Construction",
    desc: "Efficient on-site rebar bending for apartment complexes, villas and residential towers.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "High-productivity rebar operations for offices, malls, hotels and mixed-use developments.",
  },
  {
    Icon: Factory,
    title: "Industrial Projects",
    desc: "Dependable rebar bending for plants, factories and heavy industrial installations.",
  },
  {
    Icon: Landmark,
    title: "Infrastructure Projects",
    desc: "Trusted performance on metros, treatment plants, power plants and public infrastructure works.",
  },
  {
    Icon: Landmark,
    title: "Bridge Construction",
    desc: "Precision rebar bending for bridge girders, decks, piers and structural steel elements.",
  },
  {
    Icon: ArrowUp,
    title: "High Rise Buildings",
    desc: "Consistent, high-volume rebar output for tall residential and commercial towers.",
  },
  {
    Icon: Boxes,
    title: "Precast Industries",
    desc: "Reliable rebar operations for precast plants, formwork factories and modular construction.",
  },
  {
    Icon: Hammer,
    title: "Steel Fabrication",
    desc: "Continuous-duty performance for structural steel fabrication yards and rebar workshops.",
  },
];

const TRUST_CARDS = [
  {
    Icon: HardHat,
    title: "Heavy Duty Construction",
    desc: "Precision-welded steel body built for demanding site conditions and continuous industrial duty.",
  },
  {
    Icon: Target,
    title: "High Accuracy",
    desc: "Precision bending mechanism delivers consistent, repeatable angles across every bar for quality rebar output.",
  },
  {
    Icon: Award,
    title: "Premium Components",
    desc: "Trusted branded motor, gearbox and electricals from leading Indian and global brands.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every LRB Series machine is personally inspected and quality signed off before dispatch.",
  },
  {
    Icon: Flag,
    title: "Made in India",
    desc: "Designed and manufactured in India for Indian construction site conditions.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable Performance",
    desc: "Trial-run tested with safety verification for dependable performance project after project.",
  },
];

const TECH_HIGHLIGHTS = [
  {
    Icon: Cpu,
    title: "Automatic Operation",
    desc: "Fully automatic bending cycle with adjustable angle settings delivers consistent output all day long.",
  },
  {
    Icon: Package,
    title: "High Production Capacity",
    desc: "Rated at 2 tonnes per hour — delivers the throughput demanded by mid to large construction sites.",
  },
  {
    Icon: Target,
    title: "Precision Bending",
    desc: "Precision-machined bending disc and pin holds tight tolerances for accurate, repeatable bar bending.",
  },
  {
    Icon: HardHat,
    title: "Industrial Grade Construction",
    desc: "Fabricated from heavy-gauge structural steel with a sealed gearbox for continuous industrial duty.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance",
    desc: "Simple mechanical design with easy access to service points keeps operating costs low.",
  },
  {
    Icon: BadgeCheck,
    title: "Reliable Performance",
    desc: "Trial-run tested with owner inspection ensures dependable performance across every project.",
  },
];

const FAQS = [
  {
    q: "What is the maximum bar diameter that can be bent?",
    a: "The LIFTILE LRB Series can bend TMT bars up to 42 mm and plain bars up to 45 mm.\n\nLRB40 handles up to 32 mm TMT / 35 mm plain bar. LRB50 handles up to 42 mm TMT / 45 mm plain bar.",
  },
  {
    q: "Is operation automatic or manual?",
    a: "Both LRB40 and LRB50 feature fully automatic operation.\n\nOnce the required bending angle is set, the machine completes each bending cycle automatically — enabling high productivity and reduced operator fatigue during long shifts.",
  },
  {
    q: "What maintenance does the LRB Series require?",
    a: "The LRB Series uses a simple, low-maintenance mechanical design. Routine inspection of the motor, gearbox lubrication, bending disc, electrical wiring and safety systems is all that is typically required for reliable long-term operation.",
  },
  {
    q: "What motor does the LRB Series use?",
    a: "The LRB Series uses a premium industrial-grade copper winding motor.\n\nLRB40 is equipped with a 5 HP motor and LRB50 with a 6.5 HP motor. Both operate at 1440 RPM for optimal torque delivery through the reduction gearbox.",
  },
  {
    q: "How is the LRB Series installed on site?",
    a: "The LRB Series is a compact, self-contained machine. It only requires a level surface, a standard three-phase power supply and basic setup.\n\nLIFTILE provides installation guidance and commissioning support on request.",
  },
  {
    q: "Does LIFTILE provide a warranty on the Bar Bending Machine?",
    a: "Yes.\n\nEvery LIFTILE LRB Series machine is backed by our standard manufacturer's warranty on structural components, motor and gearbox. Contact our sales team for the current warranty terms applicable to your project and region.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "bar-cutting-machine",
    name: "Bar Cutting Machine",
    desc: "LRC Series heavy-duty rebar cutter for precise, high-productivity cutting of TMT and plain bars on site.",
    image: "/product-bar-cutting.jpg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact LML250 mini material lifter — ideal companion for moving rebar and cut bars to upper floors.",
    image: "/product-monkey-lift.jpg",
  },
  {
    slug: "conventional-tower-hoist",
    name: "Conventional Tower Hoist",
    desc: "LTC Series wire-rope tower hoist for dependable material lifting up to 65 m on residential and industrial projects.",
    image: "/conventional-tower-hoist-site-2.jpeg",
  },
];

const CTA_BADGES = [
  "Automatic Operation",
  "Heavy Duty Construction",
  "Premium Components",
  "Made in India",
];

/* ---------- Blueprint SVG for Download Brochure ---------- */
const LRB_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    {/* Machine base platform */}
    <rect x="80" y="220" width="240" height="30" strokeDasharray="0" />
    {/* Legs */}
    <line x1="100" y1="250" x2="100" y2="330" />
    <line x1="300" y1="250" x2="300" y2="330" />
    <line x1="90" y1="330" x2="310" y2="330" />
    {/* Ground line */}
    <line x1="60" y1="360" x2="340" y2="360" strokeDasharray="4 4" opacity="0.6" />

    {/* Motor housing (left) */}
    <rect x="90" y="150" width="70" height="70" />
    <circle cx="125" cy="185" r="14" />
    {/* Motor cooling fins */}
    {[0, 1, 2, 3].map((i) => (
      <line
        key={i}
        x1={95 + i * 15}
        y1="155"
        x2={95 + i * 15}
        y2="180"
        opacity="0.6"
      />
    ))}
    {/* Drive shaft coupling */}
    <line x1="160" y1="185" x2="200" y2="185" strokeWidth="2" />

    {/* Gearbox (middle) */}
    <rect x="200" y="165" width="50" height="55" />
    <line x1="200" y1="180" x2="250" y2="180" strokeDasharray="2 2" opacity="0.6" />
    <line x1="200" y1="205" x2="250" y2="205" strokeDasharray="2 2" opacity="0.6" />

    {/* Output shaft to bending disc */}
    <line x1="250" y1="192" x2="290" y2="192" strokeWidth="2" />

    {/* Bending disc (right) — the star of the machine */}
    <circle cx="300" cy="192" r="30" strokeWidth="1.6" />
    <circle cx="300" cy="192" r="6" />
    {/* Bending pin (offset) */}
    <circle cx="322" cy="188" r="4" />
    {/* Rebar being bent — a curve */}
    <path
      d="M 200 192 L 322 188 M 322 188 Q 340 200 340 240"
      fill="none"
      strokeWidth="2"
    />

    {/* Dimension label */}
    <text
      x="250"
      y="145"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.7"
    >
      Ø 42mm
    </text>
    {/* Component labels */}
    <text
      x="115"
      y="145"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      MOTOR
    </text>
    <text
      x="205"
      y="160"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      GEARBOX
    </text>
    <text
      x="278"
      y="240"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      BENDING DISC
    </text>
  </svg>
);

export default function BarBendingMachinePage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Bar Bending Machine (LRB Series)" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lrb-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LRB Series"
        title={
          <>
            LIFTILE Bar
            <br />
            Bending <span className="text-liftile-orange">Machine</span>
          </>
        }
        subtitle="Engineered for accurate, efficient and heavy-duty reinforcement steel bending. Designed for continuous site operation with high productivity, low maintenance and reliable performance."
        models={Object.keys(MODELS)}
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lrb-gallery"
        heading={
          <>
            LRB Series <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LRB Series Bar Bending Machine — from precision fabrication at our factory to real construction sites across India."
        items={GALLERY}
        seriesLabel="LRB Series"
        imageAlt="LIFTILE LRB Series Bar Bending Machine"
      />

      {/* ─── SECTION 3: QUICK SPECS (split layout with 2 model tabs) ─── */}
      <QuickSpecsTabs
        testId="lrb-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="The essentials — with a model selector to switch between LRB40 and LRB50 configurations."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LRB40"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lrb-features"
        heading={
          <>
            Built for Precision.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Productivity.
            </span>
          </>
        }
        subtitle="Every LIFTILE LRB Series Bar Bending Machine is engineered with premium branded components and rigorous quality inspections to deliver dependable performance on demanding construction sites."
        items={FEATURES}
      />

      {/* ─── SECTION 5: APPLICATIONS ─── */}
      <Applications
        testId="lrb-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">Construction Project</span>
          </>
        }
        subtitle="From residential builds to bridge construction and precast industries — the LRB Series is engineered for accurate, high-productivity rebar bending across every construction environment."
        items={APPLICATIONS}
        variant="dark"
        gridCols="lg:grid-cols-4"
      />

      {/* ─── SECTION 6: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lrb-why-choose"
        image="/product-bar-bending.jpg"
        imageAlt="LIFTILE LRB Series Bar Bending Machine — heavy-duty rebar bending"
        imageCaption="LRB Series — heavy-duty precision rebar bending, built for continuous site operation."
        floatingBadge={{ top: "Up to", bottom: "42 mm TMT" }}
        eyebrow="Why LRB Series"
        heading={
          <>
            Built for Heavy Duty{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">Rebar Operations</span>
          </>
        }
        subtitle="The LRB Series combines heavy-duty construction, precision bending mechanism and premium branded components to deliver dependable rebar bending on demanding construction projects."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 7: TECHNICAL HIGHLIGHTS ─── */}
      <QualitySafetyCommitment
        testId="lrb-tech-highlights"
        eyebrow="Technical Highlights"
        heading={
          <>
            Precision Engineering.{" "}
            <span className="text-liftile-orange">Industrial Grade Build.</span>
          </>
        }
        subtitle="A closer look at the engineering foundations that make the LRB Series a reliable, high-productivity rebar bending machine for modern construction sites."
        items={TECH_HIGHLIGHTS}
      />

      {/* ─── SECTION 8: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lrb-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LRB Series technical catalogue including specifications, drawings, features and product information for LRB40 and LRB50 configurations."
        blueprintSvg={LRB_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 9: FAQ ─── */}
      <PremiumFAQ
        testId="lrb-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders, contractors and rebar fabricators most often ask us about the LIFTILE LRB Series Bar Bending Machine."
        items={FAQS}
      />

      {/* ─── SECTION 10: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lrb-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 11: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lrb-product-cta"
        backgroundImage="/project-pandesara-surat.png"
        backgroundAlt="LIFTILE LRB Series Bar Bending Machine on a live construction site"
        heading={
          <>
            Precision Bending for{" "}
            <span className="text-liftile-orange">Every Project.</span>
          </>
        }
        subtitle="The LIFTILE LRB Series delivers accurate, efficient and reliable reinforcement steel bending for modern construction projects."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Contact Sales Team", href: "/contact" }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
